/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). "Contract check" section of
 * the real Accounts Payable invoice slide-in, shown in demo mode only. It reads
 * the invoice compliance analysis for the same invoice (the four AP-demo
 * invoices are in the compliance queue) and shows the findings as a compact
 * table, with the agent's resolution when there is one. Procurement and AP tell
 * one story: the early-pay clause behind the banner on AIG-90412, and the Net 45
 * terms correction on BS-22188.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { APInvoice } from '@erp/rpc';
import {
  ArrowSquareOutIcon,
  CaretDownIcon,
  CheckCircleIcon,
  SealCheckIcon,
} from '@phosphor-icons/react';
import { isApDemoMode } from '@/demo/ap/demo-mode';
import { useApEarlyPayApproved } from '@/demo/ap/provenance';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ClauseChip } from '../components/ClauseChip';
import { StatusBadge } from '../components/Badges';
import { getClause } from '../data/clauses';
import { fmtShortDate } from '../data/dates';
import { fmtMoney } from '../data/format';
import {
  getAnalysis,
  type CheckResult,
} from '../pages/compliance/compliance-engine';
import { useResolution } from '../pages/compliance/compliance-store';
import { isInvoiceId, type InvoiceId } from '../pages/compliance/invoices-data';
import { getPlan } from '../pages/compliance/recommendations';

function usd(value: number): string {
  return fmtMoney(value, { cents: true });
}

const HEAD_CLASS =
  'h-8 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground';
const LINK_CLASS =
  'inline-flex items-center gap-1 text-xs font-medium text-foreground underline-offset-4 hover:underline';

/** Check name: price rows say which item, since every price check reads alike. */
function checkName(check: CheckResult): string {
  const clause = check.clauseId ? getClause(check.clauseId) : undefined;
  return check.category === 'price' && clause ? clause.label : check.label;
}

function impactText(check: CheckResult): string {
  if (check.impactAmount > 0) return usd(check.impactAmount);
  if (check.opportunity) return `Save ${usd(check.opportunity)}`;
  return '—';
}

interface ResultCellProps {
  check: CheckResult;
  resolved: boolean;
}

function ResultCell({ check, resolved }: ResultCellProps) {
  if (check.status === 'pass') {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700">
        <CheckCircleIcon
          weight="fill"
          aria-hidden="true"
          className="size-3.5"
        />
        Pass
      </span>
    );
  }
  if (resolved) return <StatusBadge tone="success">Resolved</StatusBadge>;
  return (
    <StatusBadge tone={check.status === 'violation' ? 'danger' : 'warning'}>
      {check.status === 'violation' ? 'Violation' : 'Warning'}
    </StatusBadge>
  );
}

interface CheckRowProps {
  check: CheckResult;
  resolved: boolean;
}

function CheckRow({ check, resolved }: CheckRowProps) {
  return (
    <TableRow className="align-top text-xs">
      <TableCell className="whitespace-normal px-2 py-1.5 font-medium">
        {checkName(check)}
      </TableCell>
      <TableCell className="whitespace-normal px-2 py-1.5 tabular-nums">
        {check.invoiceValue}
      </TableCell>
      <TableCell className="whitespace-normal px-2 py-1.5 tabular-nums">
        {check.contractValue}
      </TableCell>
      <TableCell className="px-2 py-1.5">
        {check.clauseId ? (
          <ClauseChip clauseId={check.clauseId} showValue={false} />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell
        className={cn(
          'whitespace-nowrap px-2 py-1.5 text-right tabular-nums',
          check.impactAmount === 0 &&
            !check.opportunity &&
            'text-muted-foreground',
        )}
        title={check.impactNote ?? undefined}
      >
        {impactText(check)}
      </TableCell>
      <TableCell className="whitespace-nowrap px-2 py-1.5">
        <ResultCell check={check} resolved={resolved} />
      </TableCell>
    </TableRow>
  );
}

interface CallOutProps {
  invoiceId: InvoiceId;
  apInvoiceId: string;
}

/** The sentence under the header: why the section matters for this invoice. */
function CallOut({ invoiceId, apInvoiceId }: CallOutProps) {
  const analysis = getAnalysis(invoiceId);
  const plan = getPlan(invoiceId);
  const resolution = useResolution(invoiceId);
  const earlyPayApproved = useApEarlyPayApproved(apInvoiceId);
  const early = analysis.results.find(
    (entry) => entry.category === 'early_pay',
  );
  const earlyClause = early?.clauseId ? getClause(early.clauseId) : undefined;

  if (early && earlyClause && plan.kind === 'early_pay') {
    const done = earlyPayApproved || resolution !== undefined;
    return (
      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          §{earlyClause.section} {earlyClause.value}
        </span>{' '}
        is the basis for the early-payment recommendation above.{' '}
        {done
          ? `Early payment is scheduled, capturing ${usd(early.opportunity ?? 0)}.`
          : `Pay by ${fmtShortDate(plan.payOn)} to save ${usd(early.opportunity ?? 0)}.`}
      </p>
    );
  }
  if (resolution) {
    return (
      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Agent resolution.</span>{' '}
        {resolution.kind === 'override'
          ? 'Approved with override on the compliance page.'
          : plan.outcome}
      </p>
    );
  }
  return <p className="text-xs text-muted-foreground">{plan.headline}</p>;
}

interface ContractCheckCardProps {
  invoiceId: InvoiceId;
  apInvoiceId: string;
}

function ContractCheckCard({ invoiceId, apInvoiceId }: ContractCheckCardProps) {
  const analysis = getAnalysis(invoiceId);
  const resolution = useResolution(invoiceId);
  const [showPassed, setShowPassed] = useState(false);
  const contractId = analysis.invoice.contractIds[0];
  const { summary } = analysis;
  const resolved = resolution !== undefined;
  const hasFindings = analysis.flagged.length > 0;
  // With findings, the passed checks sit behind a toggle; without, show them all.
  const rows = hasFindings
    ? showPassed
      ? [...analysis.flagged, ...analysis.passed]
      : analysis.flagged
    : analysis.results;
  const findings = summary.violations + summary.warnings;

  return (
    <Card className="mx-1 gap-2 p-3" aria-label="Contract check">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <SealCheckIcon
            weight="fill"
            aria-hidden="true"
            className="size-4 text-emerald-600"
          />
          <h3 className="text-sm font-semibold leading-5">Contract check</h3>
          {hasFindings ? (
            <StatusBadge tone={resolved ? 'success' : 'danger'}>
              {findings} {findings === 1 ? 'finding' : 'findings'}
              {resolved ? ', resolved' : ''}
            </StatusBadge>
          ) : (
            <StatusBadge tone="success">All {summary.total} passed</StatusBadge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/procurement/invoice-compliance"
            search={{ invoice: invoiceId }}
            className={LINK_CLASS}
          >
            Open in invoice compliance
            <ArrowSquareOutIcon aria-hidden="true" className="size-3" />
          </Link>
          <Link
            to="/procurement/contracts/$contractId"
            params={{ contractId }}
            search={{}}
            className={LINK_CLASS}
          >
            View agreement
            <ArrowSquareOutIcon aria-hidden="true" className="size-3" />
          </Link>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Checked against{' '}
        <span className="font-mono tabular-nums text-foreground">
          {contractId}
        </span>{' '}
        · {summary.total} checks
      </p>
      <CallOut invoiceId={invoiceId} apInvoiceId={apInvoiceId} />
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className={HEAD_CLASS}>Check</TableHead>
            <TableHead className={HEAD_CLASS}>Invoice</TableHead>
            <TableHead className={HEAD_CLASS}>Agreement</TableHead>
            <TableHead className={HEAD_CLASS}>Clause</TableHead>
            <TableHead className={cn(HEAD_CLASS, 'text-right')}>
              Impact
            </TableHead>
            <TableHead className={HEAD_CLASS}>Result</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((check) => (
            <CheckRow key={check.id} check={check} resolved={resolved} />
          ))}
          {hasFindings ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={6} className="px-2 py-1">
                <button
                  type="button"
                  aria-expanded={showPassed}
                  onClick={() => setShowPassed((open) => !open)}
                  className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  <CaretDownIcon
                    aria-hidden="true"
                    className={cn(
                      'size-3 transition-transform',
                      showPassed && 'rotate-180',
                    )}
                  />
                  {summary.passed} checks passed
                </button>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </Card>
  );
}

interface ContractCheckSectionProps {
  invoice: APInvoice;
}

/**
 * Demo-only section for the AP invoice slide-in. Renders nothing outside demo
 * mode and for invoices that are not in the procurement compliance queue.
 */
export function ContractCheckSection({ invoice }: ContractCheckSectionProps) {
  if (!isApDemoMode()) return null;
  const number = invoice.invoiceNumber;
  if (!number || !isInvoiceId(number)) return null;
  return <ContractCheckCard invoiceId={number} apInvoiceId={invoice.id} />;
}
